import React from "react";
import {
  MDBContainer, MDBRow, MDBCol, MDBCard, MDBCardBody,
  MDBBtn, MDBIcon, MDBInput, MDBSpinner,
  MDBModal, MDBModalDialog, MDBModalContent,
  MDBModalHeader, MDBModalBody, MDBModalFooter,
  MDBBadge,
} from "mdb-react-ui-kit";
import {
  listFiles, trashFile, uploadFile, updateFileMetadata,
  computeFileSha256, checkDuplicate, setFileVisibility,
  mfmThumbUrl, mfmOriginalUrl, mfmMediumUrl, mfmPosterUrl, mfmFramedUrl, mfmVariantUrl,
  getTrash, restoreFile, deletePermanently,
  getSsoGroups, getFileOwners, setFileOwners, searchMfmUsers, getFileVariants,
  deleteFileVariant, deleteCustomVariants, getMfmSystemStatus,
} from "Projects/CC_cubi_clan_project/Utils/MfmService";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import "./MFMHomeView.css";

const VIEW_SIZES = [
  { key: "large",  icon: "th-large",       label: "Grande",   cols: { xs: 12, sm: 6,  md: 4,  lg: 3 } },
  { key: "medium", icon: "th",             label: "Mediano",  cols: { xs: 6,  sm: 4,  md: 3,  lg: 2 } },
  { key: "small",  icon: "grip-horizontal",label: "Pequeño",  cols: { xs: 3,  sm: 2,  md: 2,  lg: 1 } },
  { key: "list",   icon: "list",           label: "Lista",    cols: { xs: 12 } },
];
const MEDIA_TYPE_LABEL = { image: "Imagen", video: "Vídeo" };
const PAGE_SIZES = [12, 24, 48, 96];
const TRASH_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const DASH_COLORS = ["#0b6bcb", "#18a999", "#f2a541", "#e85f5c", "#7a6ff0", "#4b5563"];

export function selectPreviewVersion(versions, mediaType, excludedKeys = []) {
  const available = Array.isArray(versions)
    ? versions.filter((version) => version?.url && !excludedKeys.includes(version.key))
    : [];
  const preference = mediaType === "image"
    ? ["medium", "original", "thumb"]
    : mediaType === "video"
      ? ["poster", "original"]
      : ["original"];
  return preference.map((key) => available.find((version) => version.key === key)).find(Boolean)
    || available[0]
    || null;
}

/**
 * Formatea bytes a unidad dinámica (KB, MB, GB, TB).
 */
function formatFileSize(bytes) {
  if (bytes == null || bytes === 0) return "0 KB";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let size = bytes;
  while (size >= 1024 && i < units.length - 1) { size /= 1024; i++; }
  return (i === 0 ? size : size.toFixed(1).replace(/\.0$/, "")) + " " + units[i];
}

function parseTrashDate(value) {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === "number") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === "string") {
    const normalized = value.trim();
    let candidate = normalized;

    // Compatibilidad con formato Java ZonedDateTime: 2026-04-16T19:36:25Z[UTC]
    candidate = candidate.replace(/\[[^\]]+\]$/, "");

    let d = new Date(candidate);

    // Algunos serializados incluyen texto local entre paréntesis al final.
    if (Number.isNaN(d.getTime())) {
      const withoutParen = candidate.replace(/\s*\([^)]*\)\s*$/, "");
      d = new Date(withoutParen);
    }

    // Soporte explícito para DATETIME SQL: "YYYY-MM-DD HH:mm:ss[.S]".
    if (Number.isNaN(d.getTime())) {
      const m = candidate.match(
        /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?$/
      );
      if (m) {
        const year = Number(m[1]);
        const month = Number(m[2]) - 1;
        const day = Number(m[3]);
        const hour = Number(m[4]);
        const minute = Number(m[5]);
        const second = Number(m[6]);
        const ms = Number((m[7] || "0").slice(0, 3).padEnd(3, "0"));
        d = new Date(year, month, day, hour, minute, second, ms);
      }
    }

    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === "object") {
    if (value.$date != null) {
      return parseTrashDate(value.$date);
    }
    if (value.value != null) {
      return parseTrashDate(value.value);
    }
    if (typeof value.time === "number") {
      const d = new Date(value.time);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    // Serializado clásico de java.util.Date (year/month/date/hours/minutes/seconds).
    if (
      typeof value.year === "number" &&
      typeof value.month === "number" &&
      typeof value.date === "number"
    ) {
      const d = new Date(
        value.year + 1900,
        value.month,
        value.date,
        Number(value.hours || 0),
        Number(value.minutes || 0),
        Number(value.seconds || 0)
      );
      return Number.isNaN(d.getTime()) ? null : d;
    }
    if (
      typeof value.year === "number" &&
      typeof value.month === "number" &&
      typeof value.dayOfMonth === "number"
    ) {
      const d = new Date(value.year + 1900, value.month, value.dayOfMonth);
      return Number.isNaN(d.getTime()) ? null : d;
    }
  }

  return null;
}

/**
 * Formatea fechas recibidas desde backend soportando string, epoch y objetos serializados.
 */
function formatTrashDate(value) {
  const d = parseTrashDate(value);
  if (!d) return "-";
  return d.toLocaleDateString();
}

function formatDeletionCountdown(trashedAt, nowMs) {
  const d = parseTrashDate(trashedAt);
  if (!d) return "-";

  const remainingMs = d.getTime() + TRASH_TTL_MS - nowMs;
  if (remainingMs <= 0) return "Expirado (pendiente de limpieza)";

  const remainingSec = Math.ceil(remainingMs / 1000);
  if (remainingSec < 60) return "Se borrara en " + remainingSec + " s";

  const remainingMin = Math.ceil(remainingSec / 60);
  if (remainingMin < 60) return "Se borrara en " + remainingMin + " min";

  const remainingHours = Math.ceil(remainingMin / 60);
  if (remainingHours < 24) return "Se borrara en " + remainingHours + " h";

  const remainingDays = Math.ceil(remainingHours / 24);
  return "Se borrara en " + remainingDays + (remainingDays === 1 ? " dia" : " dias");
}

function getFileTrashedAt(file) {
  const direct = file?.trashedAt ?? file?.trashed_at ?? file?.deletedAt ?? file?.deleted_at ?? null;
  if (direct != null) return direct;

  if (!file || typeof file !== "object") return null;
  const keys = Object.keys(file);
  const candidateKey = keys.find((k) => /trashed|trash.*at|deleted.*at/i.test(k));
  return candidateKey ? file[candidateKey] : null;
}

export default class MFMHomeView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      files:       [],
      page:        0,
      pageSize:    24,
      hasMore:     false,
      search:      "",
      mediaType:   "",
      loading:     false,
      viewSize:    "medium",
      // Multi-selección
      selected:    new Set(),
      deleting:    false,
      // Subida múltiple
      uploadQueue: [],
      fileVariantsByUuid: {},
      variantDeleting: false,
      variantDeletingKey: null,
      // Edición de metadatos
      editingFile:    null,
      editTitle:      "",
      editAltText:    "",
      editTags:       "",
      editVisibility: "private",
      editGroupIds:   [],
      editGroups:     [],
      editGroupsLoading: false,
      editSaving:     false,
      // Gestión de propietarios (solo admin/manager)
      editOwners:         [],   // [{ id, name }] lista editable
      editOwnerLoading:   false,
      editOwnerQuery:     "",
      editOwnerResults:   [],
      editOwnerSearching: false,
      editOwnerSearchTimer: null,
      // Papelera
      trashFiles:  [],
      trashLoading: false,
      trashNowMs: Date.now(),
      // Descarga con selector de calidad
      downloadFile:      null,   // fichero para el modal de descarga
      downloadQuality:   "original",
      // Previsualización / compartir
      previewFile: null,
      previewVersions: [],
      previewVersion: "original",
      previewFailedVersions: [],
      previewLoaded: false,
      previewLoadError: false,
      previewCopyOk: false,
      previewCropEnabled: false,
      previewCropX: 50,
      previewCropY: 50,
      previewCropZoom: 1,
      previewCropWidth: 1246,
      previewCropHeight: 400,
      dashboardLoading: false,
      dashboardData: null,
      dashboardError: null,
    };
    this.fileInputRef = React.createRef();
    this._uploadId = 0;
    this._trashCountdownInterval = null;
  }

  componentDidMount() {
    if (this.props.viewMode === "home") {
      this.loadDashboard();
      return;
    }
    if (this.props.viewMode === "trash") {
      this.setState({ trashNowMs: Date.now() });
      this.startTrashCountdown();
      this.loadTrash();
      return;
    }
    this.loadFiles();
  }

  componentWillUnmount() {
    if (this._trashCountdownInterval) {
      clearInterval(this._trashCountdownInterval);
      this._trashCountdownInterval = null;
    }
    if (this.state.editOwnerSearchTimer) {
      clearTimeout(this.state.editOwnerSearchTimer);
    }
  }

  startTrashCountdown = () => {
    if (this._trashCountdownInterval) return;
    this._trashCountdownInterval = window.setInterval(() => {
      if (this.props.viewMode === "trash") {
        this.setState({ trashNowMs: Date.now() });
      }
    }, 1000);
  };

  stopTrashCountdown = () => {
    if (!this._trashCountdownInterval) return;
    clearInterval(this._trashCountdownInterval);
    this._trashCountdownInterval = null;
  };

  componentDidUpdate(prevProps) {
    if (prevProps.viewMode !== this.props.viewMode) {
      if (this.props.viewMode === "trash") {
        this.setState({ trashNowMs: Date.now() });
        this.startTrashCountdown();
        this.loadTrash();
      } else if (this.props.viewMode === "home") {
        this.stopTrashCountdown();
        this.loadDashboard();
      } else {
        this.stopTrashCountdown();
        this.loadFiles();
      }
    }

    const prev = prevProps.selectedLibrary;
    const next = this.props.selectedLibrary;
    const changed =
      (prev?.type     !== next?.type) ||
      (prev?.appCode  !== next?.appCode) ||
      (prev?.idLibrary !== next?.idLibrary);
    if (changed && this.props.viewMode === "home") {
      this.loadDashboard();
    }
    if (changed && this.props.viewMode !== "trash" && this.props.viewMode !== "home") {
      this.setState({ page: 0, selected: new Set() }, this.loadFiles);
    }
  }

  // â”€â”€ Files tab â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  loadDashboard = async () => {
    const { selectedLibrary } = this.props;
    const appCode = selectedLibrary?.appCode || undefined;
    this.setState({ dashboardLoading: true, dashboardError: null });
    try {
      const data = await getMfmSystemStatus(appCode);
      this.setState({ dashboardData: data || null, dashboardLoading: false });
    } catch (error) {
      this.setState({ dashboardLoading: false, dashboardError: "No se pudo cargar el estado del sistema." });
    }
  };

  loadFiles = async () => {
    this.setState({ loading: true });
    const { selectedLibrary } = this.props;
    const { search, mediaType, page, pageSize } = this.state;
    try {
      const [result, trashFiles] = await Promise.all([
        listFiles({
          search, mediaType, page, size: pageSize,
          library: selectedLibrary?.type === "library" ? selectedLibrary.idLibrary : undefined,
          app:     selectedLibrary?.type === "app"     ? selectedLibrary.appCode   : undefined,
        }),
        getTrash().catch(() => []),
      ]);

      // Fallback defensivo para entornos donde listFiles aún no devuelve trashedAt.
      const trashedUuids = new Set((trashFiles || []).map((f) => f?.uuid).filter(Boolean));
      const files = (result || []).filter((f) => !f?.trashedAt && !trashedUuids.has(f?.uuid));
      this.setState({ files, hasMore: files.length >= pageSize, selected: new Set() });
    } catch (err) {
      console.error("Error cargando ficheros:", err);
    } finally {
      this.setState({ loading: false });
    }
  };

  handleSearchChange   = (e) => this.setState({ search: e.target.value });
  handleSearch         = () => this.setState({ page: 0 }, this.loadFiles);
  handleFilterChange   = (mediaType) => this.setState({ mediaType, page: 0 }, this.loadFiles);
  handlePageSizeChange = (pageSize) => this.setState({ pageSize, page: 0 }, this.loadFiles);
  handleGoToPage       = (p) => this.setState({ page: p }, this.loadFiles);
  handlePageChange     = (delta) => this.setState((prev) => ({ page: Math.max(0, prev.page + delta) }), this.loadFiles);
  handleViewSize       = (viewSize) => this.setState({ viewSize });
    getCardImageUrl = (file, viewSize) => {
      if ((viewSize === "medium" || viewSize === "large") && file.mediumUrl != null) {
        return mfmMediumUrl(file.uuid);
      }
      return mfmThumbUrl(file.uuid);
    };

  getVideoPreviewUrl = (file) => {
    if (file?.posterUrl != null) return mfmPosterUrl(file.uuid);
    if (file?.thumbUrl != null) return mfmThumbUrl(file.uuid);
    return null;
  };

  toggleSelect = (uuid) => {
    this.setState((prev) => {
      const next = new Set(prev.selected);
      next.has(uuid) ? next.delete(uuid) : next.add(uuid);
      return { selected: next };
    });
  };
  toggleSelectAll = () => {
    const { files, selected } = this.state;
    this.setState({ selected: selected.size === files.length ? new Set() : new Set(files.map((f) => f.uuid)) });
  };

  handleTrashSingle = async (uuid) => {
    if (!window.confirm("Â¿Enviar este fichero a la papelera?")) return;
    try {
      await trashFile(uuid);
      this.loadFiles();
      this.props.onLibraryChanged?.();
    } catch (err) { console.error(err); }
  };

  handleTrashSelected = async () => {
    const { selected } = this.state;
    if (selected.size === 0) return;
    if (!window.confirm(`Â¿Enviar ${selected.size} fichero(s) a la papelera?`)) return;
    this.setState({ deleting: true });
    for (const uuid of Array.from(selected)) {
      try { await trashFile(uuid); } catch { /* continue */ }
    }
    this.setState({ deleting: false, selected: new Set() }, this.loadFiles);
    this.props.onLibraryChanged?.();
  };

  openFilePicker = () => this.fileInputRef.current?.click();

  handleFilesSelected = async (e) => {
    const { selectedLibrary } = this.props;
    if (!selectedLibrary?.appCode) return;
    const fileList = Array.from(e.target.files || []);
    e.target.value = "";
    if (!fileList.length) return;

    const appCode = selectedLibrary.appCode;
    const library = selectedLibrary.type === "library" ? selectedLibrary.slug : "_default";
    const entries = fileList.map((f) => ({
      id: ++this._uploadId,
      name: f.name,
      status: "uploading",
      progress: 0,
      error: null,
    }));
    this.setState((prev) => ({ uploadQueue: [...prev.uploadQueue, ...entries] }));

    for (let i = 0; i < fileList.length; i++) {
      const entry = entries[i];
      try {
        const contentHash = await computeFileSha256(fileList[i]);
        const dup = await checkDuplicate(contentHash, appCode, "private", [], library);
        if (dup) {
          this.setState((prev) => ({
            uploadQueue: prev.uploadQueue.map((q) => q.id === entry.id ? { ...q, status: "done", progress: 100 } : q),
          }));
          continue;
        }
        await uploadFile(fileList[i], {
          appCode,
          library,
          contentHash,
          onProgress: (pct) => {
            this.setState((prev) => ({
              uploadQueue: prev.uploadQueue.map((q) => q.id === entry.id ? { ...q, progress: pct } : q),
            }));
          },
        });
        this.setState((prev) => ({
          uploadQueue: prev.uploadQueue.map((q) => q.id === entry.id ? { ...q, status: "done", progress: 100 } : q),
        }));
      } catch (err) {
        this.setState((prev) => ({
          uploadQueue: prev.uploadQueue.map((q) => q.id === entry.id ? { ...q, status: "error", error: err.message, progress: 0 } : q),
        }));
      }
    }
    this.loadFiles();
    this.props.onLibraryChanged?.();
    setTimeout(() => {
      this.setState((prev) => ({ uploadQueue: prev.uploadQueue.filter((q) => q.status === "uploading") }));
    }, 5000);
  };

  openEdit = (e, file) => {
    e.stopPropagation();
    this.loadFileVariants(file?.uuid, true);
    this.setState({
      editingFile:    file,
      editTitle:      file.title    || "",
      editAltText:    file.altText  || "",
      editTags:       file.tags     || "",
      editVisibility: file.visibility || "private",
      editGroupIds:   Array.isArray(file.groupIds) ? file.groupIds : [],
      editGroups:         [],
      editGroupsLoading:  true,
      editOwners:         [],
      editOwnerLoading:   true,
      editOwnerQuery:     "",
      editOwnerResults:   [],
      editOwnerSearching: false,
    }, () => this.loadEditOwners(file.uuid));
    this.loadEditGroups(file.appCode);
  };
  closeEdit = () => this.setState({
    editingFile: null,
    editOwners: [],
    editOwnerQuery: "",
    editOwnerResults: [],
    variantDeleting: false,
    variantDeletingKey: null,
  });

  loadEditOwners = async (uuid) => {
    try {
      const owners = await getFileOwners(uuid);
      this.setState({ editOwners: owners || [], editOwnerLoading: false });
    } catch (_) {
      this.setState({ editOwnerLoading: false });
    }
  };

  handleAddOwner = (user) => {
    const { editOwners } = this.state;
    if (editOwners.some((o) => o.id === user.id)) return; // ya está
    const name = user.apodo || (user.nombre ? user.nombre + " " + user.apellido : String(user.id));
    this.setState({
      editOwners: [...editOwners, { id: user.id, name }],
      editOwnerQuery: "",
      editOwnerResults: [],
    });
  };

  handleRemoveOwner = (userId) => {
    this.setState((prev) => ({
      editOwners: prev.editOwners.filter((o) => o.id !== userId),
    }));
  };

  loadEditGroups = async (appCode) => {
    const code = appCode || this.props.selectedLibrary?.appCode;
    if (!code) { this.setState({ editGroupsLoading: false }); return; }
    try {
      const groups = await getSsoGroups(code);
      this.setState({ editGroups: groups || [], editGroupsLoading: false });
    } catch (err) {
      this.setState({ editGroupsLoading: false });
    }
  };

  saveEdit = async () => {
    const { editingFile, editTitle, editAltText, editTags, editVisibility, editGroupIds, editOwners } = this.state;
    if (!editingFile) return;
    this.setState({ editSaving: true });
    try {
      await updateFileMetadata(editingFile.uuid, { title: editTitle, altText: editAltText, tags: editTags });
      await setFileVisibility(editingFile.uuid, editVisibility, editGroupIds);
      if (this.props.isAdmin || this.props.isManager) {
        await setFileOwners(editingFile.uuid, editOwners.map((o) => o.id));
      }
      this.closeEdit();
      this.loadFiles();
    } catch (err) {
      console.error(err);
    } finally {
      this.setState({ editSaving: false });
    }
  };

  loadFileVariants = async (uuid, force = false) => {
    if (!uuid) return [];
    if (!force && Array.isArray(this.state.fileVariantsByUuid[uuid])) {
      return this.state.fileVariantsByUuid[uuid];
    }
    try {
      const variants = await getFileVariants(uuid);
      this.setState((prev) => ({
        fileVariantsByUuid: {
          ...prev.fileVariantsByUuid,
          [uuid]: Array.isArray(variants) ? variants : [],
        },
      }));
      return Array.isArray(variants) ? variants : [];
    } catch (error) {
      this.setState((prev) => ({
        fileVariantsByUuid: {
          ...prev.fileVariantsByUuid,
          [uuid]: [],
        },
      }));
      return [];
    }
  };

  handleDeleteVariant = async (variantKey) => {
    const { editingFile } = this.state;
    if (!editingFile?.uuid || !variantKey) return;
    if (!window.confirm(`¿Eliminar la variante ${variantKey}?`)) return;

    this.setState({ variantDeleting: true, variantDeletingKey: variantKey });
    try {
      await deleteFileVariant(editingFile.uuid, variantKey);
      await this.loadFileVariants(editingFile.uuid, true);
    } catch (error) {
      console.error("Error eliminando variante:", error);
      window.alert("No se pudo eliminar la variante.");
    } finally {
      this.setState({ variantDeleting: false, variantDeletingKey: null });
    }
  };

  handleDeleteAllCustomVariants = async () => {
    const { editingFile } = this.state;
    if (!editingFile?.uuid) return;
    if (!window.confirm("¿Eliminar todas las variantes custom de este fichero?")) return;

    this.setState({ variantDeleting: true, variantDeletingKey: "__all__" });
    try {
      await deleteCustomVariants(editingFile.uuid);
      await this.loadFileVariants(editingFile.uuid, true);
    } catch (error) {
      console.error("Error eliminando variantes custom:", error);
      window.alert("No se pudieron eliminar las variantes custom.");
    } finally {
      this.setState({ variantDeleting: false, variantDeletingKey: null });
    }
  };

  formatVariantLabel = (variant) => {
    const key = String(variant?.variantKey || "");
    const w = Number(variant?.width);
    const h = Number(variant?.height);
    const sizeSuffix = Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0
      ? ` (${Math.round(w)}x${Math.round(h)})`
      : "";

    if (key.startsWith("framed:")) {
      const shortKey = key.replace(/^framed:/, "").split(":x")[0];
      return "Custom " + shortKey + sizeSuffix;
    }
    return "Custom " + key + sizeSuffix;
  };

  buildVariantTableOptions = (file) => {
    if (!file?.uuid) return [];
    const variants = this.state.fileVariantsByUuid[file.uuid] || [];
    const options = [
      { key: "original", label: "Original", url: mfmOriginalUrl(file.uuid), canDownload: true },
    ];
    if (file.mediaType === "image") {
      if (file.mediumUrl != null) options.push({ key: "medium", label: "Media", url: mfmMediumUrl(file.uuid), canDownload: true });
      if (file.thumbUrl != null) options.push({ key: "thumb", label: "Miniatura", url: mfmThumbUrl(file.uuid), canDownload: true });
    } else if (file.mediaType === "video") {
      if (file.posterUrl != null) options.push({ key: "poster", label: "Poster", url: mfmPosterUrl(file.uuid), canDownload: true });
    }

    const reservedKeys = new Set(["original", "medium", "thumb", "poster"]);
    const existingKeys = new Set(options.map((o) => o.key));

    const dbOptions = variants
      .map((variant) => {
        const key = String(variant?.variantKey || "").trim();
        if (!key) return null;
        if (reservedKeys.has(key)) return null;

        const optionKey = "variant:" + key;
        if (existingKeys.has(optionKey)) return null;

        return {
          key: optionKey,
          label: this.formatVariantLabel(variant),
          url: mfmVariantUrl(file.uuid, key),
          canDownload: true,
        };
      })
      .filter(Boolean);

    return [...options, ...dbOptions];
  };

  getPreviewVersionOptions = (file) => {
    const dbVersions = this.buildVariantTableOptions(file);
    if (dbVersions.length > 0) return dbVersions;
    return this.getFileVersionOptions(file);
  };

  getFileVersionOptions = (file) => {
    if (!file?.uuid) return [];

    const customVariants = this.state.fileVariantsByUuid[file.uuid] || [];

    const addCustomVariants = (options) => {
      const existingKeys = new Set(options.map((o) => o.key));
      for (const variant of customVariants) {
        const key = String(variant?.variantKey || "").trim();
        if (!key) continue;
        const optionKey = "variant:" + key;
        if (existingKeys.has(optionKey)) continue;
        options.push({
          key: optionKey,
          label: this.formatVariantLabel(variant),
          url: mfmVariantUrl(file.uuid, key),
          canDownload: true,
        });
        existingKeys.add(optionKey);
      }
      return options;
    };

    if (file.mediaType === "image") {
      const options = [
        { key: "original", label: "Original", url: mfmOriginalUrl(file.uuid), canDownload: true },
      ];
      if (file.mediumUrl != null) {
        options.push({ key: "medium", label: "Media", url: mfmMediumUrl(file.uuid), canDownload: true });
      }
      if (file.thumbUrl != null) {
        options.push({ key: "thumb", label: "Miniatura", url: mfmThumbUrl(file.uuid), canDownload: true });
      }
      return addCustomVariants(options);
    }

    if (file.mediaType === "video") {
      const options = [
        { key: "original", label: "Original", url: mfmOriginalUrl(file.uuid), canDownload: true },
      ];
      if (file.posterUrl != null) {
        options.push({ key: "poster", label: "Poster", url: mfmPosterUrl(file.uuid), canDownload: true });
      }
      return addCustomVariants(options);
    }

    return addCustomVariants([{ key: "original", label: "Original", url: mfmOriginalUrl(file.uuid), canDownload: true }]);
  };

  // ── Descarga ──────────────────────────────────────────
  openDownload = (e, file) => {
    e.stopPropagation();
    this.loadFileVariants(file?.uuid, true);
    const versions = this.getFileVersionOptions(file);
    const preferred = versions.some((v) => v.key === "original") ? "original" : (versions[0]?.key || "original");
    this.setState({ downloadFile: file, downloadQuality: preferred });
  };
  closeDownload = () => this.setState({ downloadFile: null });

  openPreview = (e, file) => {
    e.stopPropagation();
    const versions = this.getPreviewVersionOptions(file);
    const selected = selectPreviewVersion(versions, file?.mediaType)?.key || "original";
    this.setState({
      previewFile: file,
      previewVersions: versions,
      previewVersion: selected,
      previewFailedVersions: [],
      previewLoaded: false,
      previewLoadError: false,
      previewCopyOk: false,
      previewCropEnabled: false,
      previewCropX: 50,
      previewCropY: 50,
      previewCropZoom: 1,
      previewCropWidth: 1246,
      previewCropHeight: 400,
    });
    this.loadFileVariants(file?.uuid, true).then(() => {
      const nextVersions = this.getPreviewVersionOptions(file);
      this.setState((prev) => {
        if (!prev.previewFile || prev.previewFile.uuid !== file.uuid) return null;
        const stillExists = nextVersions.some((v) => v.key === prev.previewVersion);
        const nextSelected = stillExists
          ? prev.previewVersion
          : (selectPreviewVersion(nextVersions, file?.mediaType)?.key || "original");
        return {
          previewVersions: nextVersions,
          previewVersion: nextSelected,
        };
      });
    });
  };

  handlePreviewLoadError = () => {
    this.setState((prev) => {
      const failed = [...new Set([...prev.previewFailedVersions, prev.previewVersion])];
      const fallback = selectPreviewVersion(prev.previewVersions, prev.previewFile?.mediaType, failed);
      return fallback
        ? { previewFailedVersions: failed, previewVersion: fallback.key, previewLoaded: false, previewLoadError: false }
        : { previewFailedVersions: failed, previewLoaded: false, previewLoadError: true };
    });
  };

  closePreview = () => this.setState({
    previewFile: null,
    previewVersions: [],
    previewVersion: "original",
    previewFailedVersions: [],
    previewLoaded: false,
    previewLoadError: false,
    previewCopyOk: false,
    previewCropEnabled: false,
    previewCropX: 50,
    previewCropY: 50,
    previewCropZoom: 1,
    previewCropWidth: 1246,
    previewCropHeight: 400,
  });

  getPreviewShareUrl = () => {
    const {
      previewFile,
      previewVersions,
      previewVersion,
      previewCropEnabled,
      previewCropX,
      previewCropY,
      previewCropZoom,
      previewCropWidth,
      previewCropHeight,
    } = this.state;

    const selected = previewVersions.find((v) => v.key === previewVersion) || previewVersions[0];
    if (!selected?.url || !previewFile?.uuid) return "";

    if (!previewCropEnabled || previewFile.mediaType !== "image") {
      return selected.url;
    }

    const width = Number(previewCropWidth);
    const height = Number(previewCropHeight);
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
      return selected.url;
    }

    const formatKey = `ratio:${Math.round(width)}:${Math.round(height)}`;
    return mfmFramedUrl(previewFile.uuid, width, height, formatKey, {
      x: Number(previewCropX),
      y: Number(previewCropY),
      zoom: Number(previewCropZoom),
    });
  };

  copyPreviewUrl = async () => {
    const url = this.getPreviewShareUrl();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      this.setState({ previewCopyOk: true });
      setTimeout(() => this.setState({ previewCopyOk: false }), 1200);
    } catch (error) {
      console.error("No se pudo copiar la URL:", error);
    }
  };

  doDownload = async () => {
    const { downloadFile, downloadQuality } = this.state;
    if (!downloadFile) return;
    const versions = this.getFileVersionOptions(downloadFile);
    const selected = versions.find((v) => v.key === downloadQuality) || versions[0];
    if (!selected?.url) return;

    try {
      const response = await fetch(selected.url);
      if (!response.ok) throw new Error("HTTP " + response.status);
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);

      const originalName = downloadFile.originalName || downloadFile.title || "download";
      const dot = originalName.lastIndexOf(".");
      const base = dot > 0 ? originalName.slice(0, dot) : originalName;
      const ext = dot > 0 ? originalName.slice(dot) : "";
      const suffix = selected.key && selected.key !== "original" ? "_" + selected.key : "";

      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = base + suffix + ext;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(objectUrl);
      this.closeDownload();
    } catch (error) {
      console.error("Error descargando fichero:", error);
    }
  };

  handleOwnerSearch = (e) => {
    const q = e.target.value;
    this.setState({ editOwnerQuery: q, editOwnerResults: [] });
    clearTimeout(this.state.editOwnerSearchTimer);
    if (q.trim().length < 2) return;
    const timer = setTimeout(async () => {
      this.setState({ editOwnerSearching: true });
      try {
        const appCode = this.state.editingFile?.appCode || this.props.selectedLibrary?.appCode;
        const results = await searchMfmUsers(q.trim(), appCode);
        this.setState({ editOwnerResults: results || [] });
      } catch (_) {
        this.setState({ editOwnerResults: [] });
      } finally {
        this.setState({ editOwnerSearching: false });
      }
    }, 400);
    this.setState({ editOwnerSearchTimer: timer });
  };;

  // â”€â”€ Trash tab â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  loadTrash = async () => {
    this.setState({ trashLoading: true });
    try {
      const trashFiles = await getTrash();
      this.setState({ trashFiles: trashFiles || [], trashNowMs: Date.now() });
    } catch (err) {
      console.error("Error cargando papelera:", err);
    } finally {
      this.setState({ trashLoading: false });
    }
  };

  handleRestore = async (uuid) => {
    try {
      await restoreFile(uuid);
      this.loadTrash();
    } catch (err) { console.error(err); }
  };

  handleDeletePermanently = async (uuid) => {
    if (!window.confirm("¿Eliminar permanentemente? Esta acción no se puede deshacer.")) return;
    try {
      await deletePermanently(uuid);
      this.loadTrash();
    } catch (err) { console.error(err); }
  };

  // â”€â”€ Pagination â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  renderPageList = (position) => {
    const { page, hasMore } = this.state;
    const start = Math.max(0, page - 4);
    const pageNums = [];
    for (let p = start; p <= page; p++) pageNums.push(p);

    return (
      <div className={`mfm-pagination mfm-pagination--${position}`}>
        <MDBBtn size="sm" color="light" disabled={page === 0} onClick={() => this.handlePageChange(-1)}>
          <MDBIcon fas icon="chevron-left" />
        </MDBBtn>
        {start > 0 && (
          <>
            <MDBBtn size="sm" color="light" onClick={() => this.handleGoToPage(0)}>1</MDBBtn>
            {start > 1 && <span className="mfm-page-ellipsis">…</span>}
          </>
        )}
        {pageNums.map((p) => (
          <MDBBtn key={p} size="sm" color={p === page ? "primary" : "light"} onClick={() => this.handleGoToPage(p)}>
            {p + 1}
          </MDBBtn>
        ))}
        <MDBBtn size="sm" color="light" disabled={!hasMore} onClick={() => this.handlePageChange(1)}>
          <MDBIcon fas icon="chevron-right" />
        </MDBBtn>
      </div>
    );
  };

  // â”€â”€ Render helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  normalizeTimelineRows = (rows = [], key) => {
    if (!Array.isArray(rows)) return [];
    return rows
      .map((r) => ({ day: String(r?.day || ""), value: Number(r?.[key] || 0) }))
      .filter((r) => r.day);
  };

  formatDayLabel = (day) => {
    const d = parseTrashDate(day);
    if (!d) return day;
    return d.toLocaleDateString(undefined, { day: "2-digit", month: "2-digit" });
  };

  buildActivitySeries = (uploadsByDay, deletionsByDay, queueByDay) => {
    const map = new Map();
    const assign = (list, key) => {
      list.forEach((item) => {
        const day = String(item.day || "");
        if (!day) return;
        if (!map.has(day)) map.set(day, { day, uploads: 0, deleted: 0, queued: 0 });
        map.get(day)[key] = Number(item.value || 0);
      });
    };

    assign(this.normalizeTimelineRows(uploadsByDay, "uploads"), "uploads");
    assign(this.normalizeTimelineRows(deletionsByDay, "deleted"), "deleted");
    assign(this.normalizeTimelineRows(queueByDay, "queued"), "queued");

    return Array.from(map.values())
      .sort((a, b) => String(a.day).localeCompare(String(b.day)))
      .slice(-30)
      .map((r) => ({ ...r, dayLabel: this.formatDayLabel(r.day) }));
  };

  renderKpiCard = (title, value, hint, tone = "neutral", icon = "chart-bar") => (
    <MDBCard className={`mfm-kpi-card mfm-kpi-card--${tone} h-100`}>
      <MDBCardBody>
        <div className="mfm-kpi-head">
          <small>{title}</small>
          <MDBIcon fas icon={icon} />
        </div>
        <div className="mfm-kpi-value">{value}</div>
        <small className="text-muted">{hint}</small>
      </MDBCardBody>
    </MDBCard>
  );

  renderHomeTab() {
    const { dashboardLoading, dashboardData, dashboardError } = this.state;

    if (dashboardLoading) {
      return (
        <div className="text-center py-5">
          <MDBSpinner className="me-2" />Cargando estado del sistema...
        </div>
      );
    }

    if (dashboardError) {
      return (
        <MDBCard className="mfm-status-error-card">
          <MDBCardBody>
            <h5 className="mb-2"><MDBIcon fas icon="exclamation-triangle" className="me-2" />Estado MFM</h5>
            <p className="mb-3">{dashboardError}</p>
            <MDBBtn size="sm" color="primary" onClick={this.loadDashboard}>Reintentar</MDBBtn>
          </MDBCardBody>
        </MDBCard>
      );
    }

    const summary = dashboardData?.summary || {};
    const queue = dashboardData?.queue || {};
    const uploadsByDay = dashboardData?.uploadsByDay || [];
    const deletionsByDay = dashboardData?.deletionsByDay || [];
    const queueByDay = dashboardData?.queueByDay || [];
    const topLibraries = dashboardData?.topLibraries || [];
    const filesByApp = dashboardData?.filesByApp || [];
    const statusBreakdown = dashboardData?.statusBreakdown || [];

    const activitySeries = this.buildActivitySeries(uploadsByDay, deletionsByDay, queueByDay);
    const queuePie = [
      { name: "Pendiente", value: Number(queue.pending || 0) },
      { name: "En progreso", value: Number(queue.inProgress || 0) },
      { name: "Fallidos", value: Number(queue.failed || 0) },
      { name: "Completados", value: Number(queue.completed || 0) },
    ];
    const hasQueueData = queuePie.some((i) => i.value > 0);

    return (
      <>
        <MDBRow className="mb-3 align-items-center">
          <MDBCol md="8">
            <h5 className="mb-0"><MDBIcon fas icon="gauge-high" className="me-2" />Estado del sistema MFM</h5>
            <small className="text-muted">Resumen operativo e histórico extraído en tiempo real sin alterar la BBDD.</small>
          </MDBCol>
          <MDBCol md="4" className="text-md-end mt-2 mt-md-0">
            <MDBBtn size="sm" color="light" onClick={this.loadDashboard}>
              <MDBIcon fas icon="arrows-rotate" className="me-1" />Actualizar
            </MDBBtn>
          </MDBCol>
        </MDBRow>

        <MDBRow className="g-3 mb-3">
          <MDBCol xs="12" sm="6" lg="3">{this.renderKpiCard("Ficheros totales", Number(summary.totalFiles || 0), "Inventario actual", "primary", "database")}</MDBCol>
          <MDBCol xs="12" sm="6" lg="3">{this.renderKpiCard("En proceso", Number(summary.processingFiles || 0), "Estado processing", Number(summary.processingFiles || 0) > 0 ? "warning" : "neutral", "cogs")}</MDBCol>
          <MDBCol xs="12" sm="6" lg="3">{this.renderKpiCard("Eliminados", Number(summary.deletedFiles || 0), "En papelera global", "danger", "trash")}</MDBCol>
          <MDBCol xs="12" sm="6" lg="3">{this.renderKpiCard("Pendientes", Number(queue.pending || 0), "Cola de transcodificación", Number(queue.pending || 0) > 0 ? "warning" : "ok", "clock")}</MDBCol>
        </MDBRow>

        <MDBRow className="g-3 mb-3">
          <MDBCol xs="12" md="6" xl="3">{this.renderKpiCard("Listos", Number(summary.readyFiles || 0), "Disponibles para uso", "ok", "check-circle")}</MDBCol>
          <MDBCol xs="12" md="6" xl="3">{this.renderKpiCard("Con error", Number(summary.errorFiles || 0), "Estado error", Number(summary.errorFiles || 0) > 0 ? "danger" : "neutral", "triangle-exclamation")}</MDBCol>
          <MDBCol xs="12" md="6" xl="3">{this.renderKpiCard("Vídeos sin thumb", Number(summary.videosWithoutThumb || 0), "Conviene revisar históricos", Number(summary.videosWithoutThumb || 0) > 0 ? "warning" : "ok", "image")}</MDBCol>
          <MDBCol xs="12" md="6" xl="3">{this.renderKpiCard("Usos activos", Number(summary.totalUsages || 0), "Referencias en entidades", "primary", "link")}</MDBCol>
        </MDBRow>

        <MDBRow className="g-3 mb-3">
          <MDBCol xs="12" xl="8">
            <MDBCard className="h-100">
              <MDBCardBody>
                <h6 className="mb-3">Actividad diaria (30 días)</h6>
                <div className="mfm-chart-box">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={activitySeries}>
                      <defs>
                        <linearGradient id="mfmUploads" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#0b6bcb" stopOpacity={0.35} />
                          <stop offset="95%" stopColor="#0b6bcb" stopOpacity={0.03} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="dayLabel" />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      <Area type="monotone" dataKey="uploads" stroke="#0b6bcb" fill="url(#mfmUploads)" strokeWidth={2} name="Subidas" />
                      <Line type="monotone" dataKey="deleted" stroke="#e85f5c" strokeWidth={2} name="Eliminados" dot={false} />
                      <Line type="monotone" dataKey="queued" stroke="#f2a541" strokeWidth={2} name="Encolados" dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>

          <MDBCol xs="12" xl="4">
            <MDBCard className="h-100">
              <MDBCardBody>
                <h6 className="mb-3">Estado de cola</h6>
                <div className="mfm-chart-box mfm-chart-box--small">
                  {hasQueueData ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={queuePie} dataKey="value" nameKey="name" innerRadius={54} outerRadius={88} paddingAngle={2}>
                          {queuePie.map((entry, idx) => <Cell key={entry.name} fill={DASH_COLORS[idx % DASH_COLORS.length]} />)}
                        </Pie>
                        <Tooltip />
                        <Legend verticalAlign="bottom" height={36} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="mfm-chart-empty">No hay datos de cola todavía.</div>
                  )}
                </div>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
        </MDBRow>

        <MDBRow className="g-3 mb-3">
          <MDBCol xs="12" xl="6">
            <MDBCard className="h-100">
              <MDBCardBody>
                <h6 className="mb-3">Ficheros por app</h6>
                <div className="mfm-chart-box mfm-chart-box--small">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={filesByApp} layout="vertical" margin={{ left: 8, right: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" allowDecimals={false} />
                      <YAxis type="category" dataKey="appCode" width={60} />
                      <Tooltip />
                      <Bar dataKey="files" fill="#18a999" radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
          <MDBCol xs="12" xl="6">
            <MDBCard className="h-100">
              <MDBCardBody>
                <h6 className="mb-3">Distribución por estado</h6>
                <div className="mfm-chart-box mfm-chart-box--small">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={statusBreakdown}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="status" />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#4c6ef5" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
        </MDBRow>

        <MDBRow className="g-3">
          <MDBCol xs="12" xl="7">
            <MDBCard className="h-100">
              <MDBCardBody>
                <h6 className="mb-3">Top bibliotecas por volumen</h6>
                <div className="table-responsive">
                  <table className="table table-sm align-middle mfm-status-table">
                    <thead>
                      <tr>
                        <th>App</th>
                        <th>Biblioteca</th>
                        <th className="text-end">Ficheros</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topLibraries.length === 0 && (
                        <tr><td colSpan="3" className="text-center text-muted py-3">Sin datos</td></tr>
                      )}
                      {topLibraries.map((row, idx) => (
                        <tr key={`${row.appCode || "-"}-${row.libraryName || "-"}-${idx}`}>
                          <td>{row.appCode || "-"}</td>
                          <td>{row.libraryName || "-"}</td>
                          <td className="text-end fw-semibold">{Number(row.files || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
          <MDBCol xs="12" xl="5">
            <MDBCard className="h-100">
              <MDBCardBody>
                <h6 className="mb-3">Indicadores rápidos</h6>
                <ul className="mfm-quick-facts">
                  <li><span>Total bibliotecas</span><strong>{Number(summary.totalLibraries || 0)}</strong></li>
                  <li><span>Imágenes</span><strong>{Number(summary.imageFiles || 0)}</strong></li>
                  <li><span>Vídeos</span><strong>{Number(summary.videoFiles || 0)}</strong></li>
                  <li><span>Owners activos</span><strong>{Number(summary.activeOwners || 0)}</strong></li>
                  <li><span>Owners en papelera</span><strong>{Number(summary.trashedOwners || 0)}</strong></li>
                </ul>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
        </MDBRow>
      </>
    );
  }

  renderVisibilityBadge(visibility) {
    const map = {
      private: { color: "secondary", label: "Privado" },
      groups:  { color: "warning",   label: "Grupos"  },
      app:     { color: "info",      label: "App"     },
      public:  { color: "success",   label: "Público" },
    };
    const v = map[visibility] || { color: "light", label: visibility };
    return <MDBBadge color={v.color} pill style={{ fontSize: "0.6rem" }}>{v.label}</MDBBadge>;
  }

  // â”€â”€ Render: files tab â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  renderFilesTab() {
    const {
      files, loading, search, mediaType, pageSize,
      selected, deleting, viewSize, uploadQueue,
    } = this.state;

    const allSelected = files.length > 0 && selected.size === files.length;
    const someSelected = selected.size > 0;
    const viewCfg  = VIEW_SIZES.find((v) => v.key === viewSize) || VIEW_SIZES[1];
    const isList   = viewSize === "list";
    const canUpload = !!this.props.selectedLibrary?.appCode;

    return (
      <>
        {/* Cabecera */}
        <MDBRow className="mb-3 align-items-center">
          <MDBCol md="5">
            <h5 className="mb-0">
              <MDBIcon fas icon="photo-video" className="me-2" />
              Ficheros
            </h5>
          </MDBCol>
          <MDBCol md="7" className="d-flex justify-content-end align-items-center gap-2 flex-wrap">
            <div className="mfm-view-toggle d-flex gap-1">
              {VIEW_SIZES.map((v) => (
                <MDBBtn key={v.key} size="sm" color={viewSize === v.key ? "primary" : "light"} title={v.label} onClick={() => this.handleViewSize(v.key)}>
                  <MDBIcon fas icon={v.icon} />
                </MDBBtn>
              ))}
            </div>
            <div className="d-flex align-items-center gap-1">
              <small className="text-muted text-nowrap">Por pág.:</small>
              <select className="form-select form-select-sm" style={{ width: 70 }} value={pageSize} onChange={(e) => this.handlePageSizeChange(Number(e.target.value))}>
                {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <>
              <MDBBtn
                size="sm"
                color="success"
                onClick={this.openFilePicker}
                disabled={!canUpload}
                title={canUpload ? "Subir ficheros" : "Selecciona una biblioteca para subir"}
              >
                <MDBIcon fas icon="upload" className="me-1" />Subir
              </MDBBtn>
              <input ref={this.fileInputRef} type="file" multiple accept="image/*,video/*" style={{ display: "none" }} onChange={this.handleFilesSelected} />
              {!canUpload && (
                <span className="text-muted" style={{ fontSize: "0.85rem" }}>Selecciona una biblioteca para subir</span>
              )}
            </>
          </MDBCol>
        </MDBRow>

        {/* Cola subida */}
        {uploadQueue.length > 0 && (
          <div className="mfm-upload-queue mb-3">
            {uploadQueue.map((item) => (
              <div key={item.id} className={`mfm-upload-item mfm-upload-item--${item.status}`}>
                <div className="mfm-upload-item-head">
                  <div className="d-flex align-items-center gap-1 min-w-0">
                    {item.status === "uploading" && <MDBSpinner size="sm" className="me-1" />}
                    {item.status === "done"      && <MDBIcon fas icon="check-circle" className="me-1 text-success" />}
                    {item.status === "error"     && <MDBIcon fas icon="times-circle" className="me-1 text-danger" />}
                    <span className="text-truncate">{item.name}</span>
                  </div>
                  <small className="mfm-upload-pct">{item.status === "done" ? "100%" : `${item.progress || 0}%`}</small>
                </div>
                <div className="mfm-upload-progress">
                  <div
                    className={`mfm-upload-progress-bar mfm-upload-progress-bar--${item.status}`}
                    style={{ width: `${item.status === "done" ? 100 : (item.progress || 0)}%` }}
                  />
                </div>
                {item.error && <small className="text-danger">{item.error}</small>}
              </div>
            ))}
          </div>
        )}

        {/* Filtros */}
        <MDBRow className="mb-3 align-items-center">
          <MDBCol xs="12" sm="5" md="4">
            <MDBInput label="Buscar..." value={search} onChange={this.handleSearchChange} onKeyDown={(e) => e.key === "Enter" && this.handleSearch()} />
          </MDBCol>
          <MDBCol xs="12" sm="5" md="5" className="d-flex gap-2 mt-2 mt-sm-0">
            <MDBBtn size="sm" color={mediaType === ""      ? "primary" : "light"} onClick={() => this.handleFilterChange("")}     >Todos</MDBBtn>
            <MDBBtn size="sm" color={mediaType === "image" ? "primary" : "light"} onClick={() => this.handleFilterChange("image")}>Imágenes</MDBBtn>
            <MDBBtn size="sm" color={mediaType === "video" ? "primary" : "light"} onClick={() => this.handleFilterChange("video")}>Vídeos</MDBBtn>
          </MDBCol>
          <MDBCol xs="12" sm="2" md="3" className="d-flex justify-content-end align-items-center gap-2 mt-2 mt-sm-0">
            {files.length > 0 && (
              <label className="d-flex align-items-center gap-1" style={{ cursor: "pointer", fontSize: "0.85rem" }}>
                <input type="checkbox" checked={allSelected} onChange={this.toggleSelectAll} />
                Todo
              </label>
            )}
            {someSelected && (
              <MDBBtn size="sm" color="warning" onClick={this.handleTrashSelected} disabled={deleting}>
                {deleting ? <MDBSpinner size="sm" className="me-1" /> : <MDBIcon fas icon="trash" className="me-1" />}
                Papelera ({selected.size})
              </MDBBtn>
            )}
          </MDBCol>
        </MDBRow>

        {files.length > 0 && this.renderPageList("top")}

        {loading ? (
          <div className="text-center py-5"><MDBIcon fas icon="spinner" spin size="2x" /></div>
        ) : files.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <MDBIcon fas icon="inbox" size="3x" className="mb-3" />
            <p>No hay ficheros</p>
          </div>
        ) : (
          <MDBRow>
            {files.map((file) => {
              const isSelected = selected.has(file.uuid);
              const isProcessingVideo = file.mediaType === "video" && file.status === "processing";
              return (
                <MDBCol key={file.uuid} {...viewCfg.cols} className="mb-3">
                  <MDBCard className={`mfm-file-card h-100${isSelected ? " mfm-file-card--selected" : ""}${isList ? " mfm-file-card--list" : ""}`} onClick={() => this.toggleSelect(file.uuid)} style={{ cursor: "pointer" }}>
                    {isList ? (
                      <div className="mfm-file-card-list-inner">
                        <div className="mfm-file-thumb-sm">
                          {isSelected && <div className="mfm-file-check"><MDBIcon fas icon="check-circle" /></div>}
                          {file.mediaType === "image"
                            ? <img src={mfmThumbUrl(file.uuid)} alt={file.title || file.originalName} />
                            : isProcessingVideo
                                ? <div className="mfm-video-processing mfm-video-processing--small"><MDBSpinner size="sm" /></div>
                            : (this.getVideoPreviewUrl(file)
                                ? <img src={this.getVideoPreviewUrl(file)} alt={file.title || file.originalName} />
                                : <MDBIcon fas icon="play-circle" size="2x" />)}
                        </div>
                        <div className="mfm-file-list-info">
                          <span className="text-truncate fw-semibold">{file.title || file.originalName}</span>
                          <small className="text-muted">{MEDIA_TYPE_LABEL[file.mediaType] || file.mediaType} — {formatFileSize(file.fileSize)}</small>
                          <small className="text-muted"><MDBIcon fas icon="user" className="me-1" />{file.uploaderName || "Sin propietario"}</small>
                          <span className="ms-1">{this.renderVisibilityBadge(file.visibility)}</span>
                        </div>
                        <div className="mfm-file-actions-list" onClick={(e) => e.stopPropagation()}>
                          <button onClick={(e) => this.openEdit(e, file)} title="Editar"><MDBIcon fas icon="pen" /></button>
                          <button onClick={(e) => this.openPreview(e, file)} title="Abrir"><MDBIcon fas icon="external-link-alt" /></button>
                          <button onClick={(e) => this.openDownload(e, file)} title="Descargar"><MDBIcon fas icon="download" /></button>
                          <button onClick={() => this.handleTrashSingle(file.uuid)} title="Enviar a papelera"><MDBIcon fas icon="trash" /></button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="mfm-file-thumb">
                          {isSelected && <div className="mfm-file-check"><MDBIcon fas icon="check-circle" /></div>}
                          {file.mediaType === "image"
                            ? <img src={this.getCardImageUrl(file, viewSize)} alt={file.title || file.originalName} />
                            : isProcessingVideo
                                ? <div className="mfm-video-processing"><MDBSpinner /></div>
                            : (this.getVideoPreviewUrl(file)
                                ? <img src={this.getVideoPreviewUrl(file)} alt={file.title || file.originalName} />
                                : <div className="mfm-video-icon"><MDBIcon fas icon="play-circle" size="3x" /></div>)}
                          <div className="mfm-file-actions" onClick={(e) => e.stopPropagation()}>
                            <button onClick={(e) => this.openEdit(e, file)} title="Editar"><MDBIcon fas icon="pen" /></button>
                            <button onClick={(e) => this.openPreview(e, file)} title="Abrir"><MDBIcon fas icon="external-link-alt" /></button>
                            <button onClick={(e) => this.openDownload(e, file)} title="Descargar"><MDBIcon fas icon="download" /></button>
                            <button onClick={() => this.handleTrashSingle(file.uuid)} title="Papelera"><MDBIcon fas icon="trash" /></button>
                          </div>
                        </div>
                        <MDBCardBody className="p-2">
                          <small className="text-truncate d-block" title={file.originalName}>{file.title || file.originalName}</small>
                          <small className="text-muted">{MEDIA_TYPE_LABEL[file.mediaType] || file.mediaType} — {formatFileSize(file.fileSize)}</small>
                          <small className="text-muted d-block"><MDBIcon fas icon="user" className="me-1" />{file.uploaderName || "Sin propietario"}</small>
                          <span className="ms-1">{this.renderVisibilityBadge(file.visibility)}</span>
                        </MDBCardBody>
                      </>
                    )}
                  </MDBCard>
                </MDBCol>
              );
            })}
          </MDBRow>
        )}

        {this.renderPageList("bottom")}
      </>
    );
  }

  // â”€â”€ Render: trash tab â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  renderTrashTab() {
    const { trashFiles, trashLoading, trashNowMs } = this.state;
    return (
      <>
        <MDBRow className="mb-3 align-items-center">
          <MDBCol>
            <h5><MDBIcon fas icon="trash-alt" className="me-2" />Papelera</h5>
            <small className="text-muted">Los ficheros se eliminan permanentemente a los 30 días.</small>
          </MDBCol>
          <MDBCol className="text-end">
            <MDBBtn size="sm" color="light" onClick={this.loadTrash}><MDBIcon fas icon="sync" /></MDBBtn>
          </MDBCol>
        </MDBRow>

        {trashLoading ? (
          <div className="text-center py-4"><MDBSpinner /></div>
        ) : trashFiles.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <MDBIcon fas icon="check-circle" size="2x" className="mb-2 text-success" />
            <p>La papelera está vacía</p>
          </div>
        ) : (
          trashFiles.map((file) => (
            <MDBCard key={file.uuid} className="mb-2">
              <MDBCardBody className="d-flex align-items-center gap-3 py-2">
                {(() => {
                  const trashedAt = getFileTrashedAt(file);
                  return (
                    <>
                <div style={{ width: 48, height: 48, flexShrink: 0, background: "#eee", borderRadius: 4, overflow: "hidden" }}>
                  {file.mediaType === "image"
                    ? <img src={mfmThumbUrl(file.uuid)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : <div className="d-flex align-items-center justify-content-center h-100"><MDBIcon fas icon="play-circle" /></div>}
                </div>
                <div className="flex-grow-1 min-w-0">
                  <div className="text-truncate fw-semibold">{file.title || file.originalName}</div>
                  <small className="text-muted">
                    Eliminado: {formatTrashDate(trashedAt)}
                    &nbsp;·&nbsp;
                    {formatFileSize(file.fileSize)}
                  </small>
                  <small className="text-muted d-block">
                    {formatDeletionCountdown(trashedAt, trashNowMs)}
                  </small>
                  <small className="text-muted d-block">
                    {file.appCode || "-"}/{file.libraryName || "-"}
                  </small>
                </div>
                <div className="d-flex gap-2 flex-shrink-0">
                  <MDBBtn size="sm" color="success" outline onClick={() => this.handleRestore(file.uuid)}>
                    <MDBIcon fas icon="undo" className="me-1" />Restaurar
                  </MDBBtn>
                  <MDBBtn size="sm" color="danger" outline onClick={() => this.handleDeletePermanently(file.uuid)}>
                    <MDBIcon fas icon="times" className="me-1" />Eliminar
                  </MDBBtn>
                </div>
                    </>
                  );
                })()}
              </MDBCardBody>
            </MDBCard>
          ))
        )}
      </>
    );
  }

  // â”€â”€ Main render â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  render() {
    const { editingFile, editTitle, editAltText, editTags, editSaving, editVisibility, editGroupIds, editGroups, editGroupsLoading,
            editOwners, editOwnerLoading, editOwnerQuery, editOwnerResults, editOwnerSearching,
            variantDeleting, variantDeletingKey } = this.state;
    const { isAdmin, isManager } = this.props;
    const isTrashView = this.props.viewMode === "trash";
    const isHomeView = this.props.viewMode === "home";

    return (
      <MDBContainer fluid className="mfm-home p-4">

        {isHomeView ? this.renderHomeTab() : (isTrashView ? this.renderTrashTab() : this.renderFilesTab())}

        {/* Modal editar metadatos (global) */}
        <MDBModal show={!!editingFile} onHide={this.closeEdit} tabIndex="-1">
          <MDBModalDialog size="sm">
            <MDBModalContent>
              <MDBModalHeader>
                <h5 className="modal-title">Editar metadatos</h5>
                <MDBBtn className="btn-close" color="none" onClick={this.closeEdit} />
              </MDBModalHeader>
              <MDBModalBody>
                {editingFile && (
                  <div className="mb-3 pb-2 border-bottom">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <small className="fw-semibold d-block">Versiones disponibles</small>
                      <MDBBtn
                        size="sm"
                        color="danger"
                        outline
                        disabled={variantDeleting}
                        onClick={this.handleDeleteAllCustomVariants}
                      >
                        {variantDeletingKey === "__all__" ? <MDBSpinner size="sm" className="me-1" /> : <MDBIcon fas icon="trash" className="me-1" />}
                        Borrar custom
                      </MDBBtn>
                    </div>
                    <div className="d-flex flex-column gap-1">
                      {this.getFileVersionOptions(editingFile).map((v) => {
                        const isCustom = v.key.startsWith("variant:");
                        const variantKey = isCustom ? v.key.replace(/^variant:/, "") : null;
                        const deletingThis = variantDeleting && variantDeletingKey === variantKey;
                        return (
                          <div key={v.key} className="d-flex align-items-center justify-content-between gap-2">
                            <MDBBadge color="light" className="text-dark border">{v.label}</MDBBadge>
                            {isCustom && (
                              <MDBBtn
                                size="sm"
                                color="danger"
                                outline
                                disabled={variantDeleting}
                                onClick={() => this.handleDeleteVariant(variantKey)}
                              >
                                {deletingThis ? <MDBSpinner size="sm" className="me-1" /> : <MDBIcon fas icon="times" className="me-1" />}
                                Eliminar
                              </MDBBtn>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <MDBInput label="Título" value={editTitle} onChange={(e) => this.setState({ editTitle: e.target.value })} className="mb-3" />
                <MDBInput label="Alt text" value={editAltText} onChange={(e) => this.setState({ editAltText: e.target.value })} className="mb-3" />
                <MDBInput label="Tags (separados por coma)" value={editTags} onChange={(e) => this.setState({ editTags: e.target.value })} className="mb-3" />
                <div>
                  <label className="form-label mb-1"><small className="fw-semibold">Visibilidad</small></label>
                  <div className="d-flex gap-3 flex-wrap">
                    {[
                      { v: "private", label: "Privado" },
                      { v: "app",     label: "App"     },
                      { v: "public",  label: "Público" },
                      { v: "groups",  label: "Grupos"  },
                    ].map(({ v, label }) => (
                      <label key={v} className="d-flex align-items-center gap-1" style={{ cursor: "pointer" }}>
                        <input type="radio" name="editVisibility" value={v} checked={editVisibility === v} onChange={() => this.setState({ editVisibility: v })} />
                        <small>{label}</small>
                      </label>
                    ))}
                  </div>
                  {editVisibility === "groups" && (
                    <div className="mt-2 ms-1">
                      <small className="text-muted d-block mb-1">Grupos con acceso:</small>
                      {editGroupsLoading ? (
                        <small className="text-muted"><MDBSpinner size="sm" className="me-1" />Cargando grupos...</small>
                      ) : editGroups.length === 0 ? (
                        <small className="text-muted">No hay grupos disponibles para esta app.</small>
                      ) : (
                        editGroups.map((g) => (
                          <label key={g.idGroup} className="d-flex align-items-center gap-1 mb-1" style={{ cursor: "pointer", fontSize: "0.85rem" }}>
                            <input
                              type="checkbox"
                              checked={editGroupIds.includes(g.idGroup)}
                              onChange={(ev) => {
                                const ids = editGroupIds.slice();
                                if (ev.target.checked) { if (!ids.includes(g.idGroup)) ids.push(g.idGroup); }
                                else { const i = ids.indexOf(g.idGroup); if (i >= 0) ids.splice(i, 1); }
                                this.setState({ editGroupIds: ids });
                              }}
                            />
                            {g.name}
                          </label>
                        ))
                      )}
                    </div>
                  )}
                </div>

                {/* Gestión de propietarios — solo APP_ADMIN o APP_MANAGER */}
                {(isAdmin || isManager) && (
                  <div className="mt-3 border-top pt-3">
                    <label className="form-label mb-1"><small className="fw-semibold">Propietarios</small></label>

                    {/* Lista actual de propietarios */}
                    {editOwnerLoading ? (
                      <div className="text-muted" style={{ fontSize: "0.82rem" }}>
                        <MDBSpinner size="sm" className="me-1" />Cargando propietarios...
                      </div>
                    ) : (
                      <ul className="list-group list-group-flush mb-2" style={{ fontSize: "0.85rem" }}>
                        {editOwners.length === 0 && (
                          <li className="list-group-item py-1 px-0 text-muted">Sin propietarios registrados</li>
                        )}
                        {editOwners.map((o) => (
                          <li key={o.id} className="list-group-item py-1 px-0 d-flex align-items-center justify-content-between">
                            <span><MDBIcon fas icon="user" className="me-1 text-secondary" />{o.name}</span>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger py-0 px-1"
                              style={{ fontSize: "0.72rem" }}
                              onClick={() => this.handleRemoveOwner(o.id)}
                              title="Eliminar propietario"
                            >
                              <MDBIcon fas icon="times" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Buscador + botón añadir */}
                    <div className="d-flex gap-2 align-items-start">
                      <div className="flex-grow-1">
                        <MDBInput
                          label="Buscar usuario para añadir..."
                          value={editOwnerQuery}
                          onChange={this.handleOwnerSearch}
                          size="sm"
                        />
                      </div>
                    </div>
                    {editOwnerSearching && (
                      <small className="text-muted"><MDBSpinner size="sm" className="me-1" />Buscando...</small>
                    )}
                    {editOwnerResults.length > 0 && (
                      <ul className="list-group mt-1" style={{ maxHeight: 150, overflowY: "auto", fontSize: "0.85rem" }}>
                        {editOwnerResults.map((u) => (
                          <li
                            key={u.id}
                            className="list-group-item list-group-item-action py-1 px-2 d-flex align-items-center justify-content-between"
                          >
                            <span>
                              <strong>{u.apodo}</strong>
                              {u.nombre && <span className="text-muted ms-1">{u.nombre} {u.apellido}</span>}
                            </span>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary py-0 px-2"
                              style={{ fontSize: "0.72rem" }}
                              onClick={() => this.handleAddOwner(u)}
                              disabled={editOwners.some((o) => o.id === u.id)}
                            >
                              {editOwners.some((o) => o.id === u.id) ? "Añadido" : "Añadir"}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    {!editOwnerSearching && editOwnerQuery.length >= 2 && editOwnerResults.length === 0 && (
                      <small className="text-muted">Sin resultados</small>
                    )}
                  </div>
                )}
              </MDBModalBody>
              <MDBModalFooter>
                <MDBBtn color="secondary" size="sm" onClick={this.closeEdit} disabled={editSaving}>Cancelar</MDBBtn>
                <MDBBtn color="primary" size="sm" onClick={this.saveEdit} disabled={editSaving}>
                  {editSaving ? <MDBSpinner size="sm" className="me-1" /> : null}
                  Guardar
                </MDBBtn>
              </MDBModalFooter>
            </MDBModalContent>
          </MDBModalDialog>
        </MDBModal>

        {/* Modal descarga con selector de calidad */}
        <MDBModal show={!!this.state.downloadFile} onHide={this.closeDownload} tabIndex="-1">
          <MDBModalDialog size="sm">
            <MDBModalContent>
              <MDBModalHeader>
                <h5 className="modal-title"><MDBIcon fas icon="download" className="me-2" />Descargar</h5>
                <MDBBtn className="btn-close" color="none" onClick={this.closeDownload} />
              </MDBModalHeader>
              <MDBModalBody>
                <p className="mb-2 text-truncate" style={{ fontSize: "0.85rem" }}>
                  <strong>{this.state.downloadFile?.title || this.state.downloadFile?.originalName}</strong>
                </p>
                <label className="form-label mb-1"><small className="fw-semibold">Calidad</small></label>
                <div className="d-flex flex-column gap-2">
                  {this.getFileVersionOptions(this.state.downloadFile || {}).map(({ key, label }) => (
                    <label key={key} className="d-flex align-items-start gap-2" style={{ cursor: "pointer" }}>
                      <input
                        type="radio"
                        name="downloadQuality"
                        value={key}
                        checked={this.state.downloadQuality === key}
                        onChange={() => this.setState({ downloadQuality: key })}
                        className="mt-1"
                      />
                      <div>
                        <small className="fw-semibold">{label}</small>
                      </div>
                    </label>
                  ))}
                </div>
              </MDBModalBody>
              <MDBModalFooter>
                <MDBBtn color="secondary" size="sm" onClick={this.closeDownload}>Cancelar</MDBBtn>
                <MDBBtn color="primary" size="sm" onClick={this.doDownload}>
                  <MDBIcon fas icon="download" className="me-1" />Descargar
                </MDBBtn>
              </MDBModalFooter>
            </MDBModalContent>
          </MDBModalDialog>
        </MDBModal>

        {/* Modal abrir / compartir */}
        <MDBModal show={!!this.state.previewFile} onHide={this.closePreview} tabIndex="-1">
          <MDBModalDialog size="lg">
            <MDBModalContent>
              <MDBModalHeader>
                <h5 className="modal-title"><MDBIcon fas icon="external-link-alt" className="me-2" />Previsualizar y compartir</h5>
                <MDBBtn className="btn-close" color="none" onClick={this.closePreview} />
              </MDBModalHeader>
              <MDBModalBody>
                {(() => {
                  const {
                    previewFile, previewVersions, previewVersion,
                    previewCropEnabled, previewCropX, previewCropY, previewCropZoom,
                    previewCropWidth, previewCropHeight,
                  } = this.state;
                  const selected = previewVersions.find((v) => v.key === previewVersion) || previewVersions[0] || null;
                  const shareUrl = this.getPreviewShareUrl();
                  const aspect = Number(previewCropWidth) > 0 && Number(previewCropHeight) > 0
                    ? `${previewCropWidth} / ${previewCropHeight}`
                    : "16 / 9";
                  const imagePreviewStyle = previewCropEnabled && previewFile?.mediaType === "image"
                    ? {
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      objectPosition: `${previewCropX}% ${previewCropY}%`,
                      transform: `scale(${previewCropZoom})`,
                      transformOrigin: `${previewCropX}% ${previewCropY}%`,
                    }
                    : {
                      width: "100%",
                      height: "auto",
                      maxHeight: 460,
                      objectFit: "contain",
                      display: "block",
                      margin: "0 auto",
                    };
                  return (
                    <>
                      <div className="mb-2">
                        <small className="fw-semibold d-block mb-1">Versión</small>
                        <div className="d-flex flex-wrap gap-2">
                          {previewVersions.map((v) => (
                            <MDBBtn
                              key={v.key}
                              size="sm"
                              color={previewVersion === v.key ? "primary" : "light"}
                              onClick={() => this.setState({
                                previewVersion: v.key,
                                previewCopyOk: false,
                                previewFailedVersions: [],
                                previewLoaded: false,
                                previewLoadError: false,
                              })}
                            >
                              {v.label}
                            </MDBBtn>
                          ))}
                        </div>
                      </div>

                      {previewFile?.mediaType === "image" && (
                        <div className="mb-3">
                          <MDBBtn
                            size="sm"
                            color={previewCropEnabled ? "warning" : "light"}
                            onClick={() => this.setState({ previewCropEnabled: !previewCropEnabled })}
                          >
                            <MDBIcon fas icon="crop" className="me-1" />
                            {previewCropEnabled ? "Ocultar frame de recorte" : "Mostrar frame de recorte"}
                          </MDBBtn>
                        </div>
                      )}

                      <div className="mb-3 p-2 border rounded" style={{ minHeight: 240, background: "#f8f9fa" }}>
                        {previewFile?.mediaType === "image" && selected?.url && (
                          <div style={{ width: "100%", maxHeight: 460, margin: "0 auto", overflow: "hidden" }}>
                            <div style={{ width: "100%", aspectRatio: previewCropEnabled ? aspect : undefined, maxHeight: 460, overflow: "hidden", border: previewCropEnabled ? "2px dashed #3b71ca" : "none" }}>
                              {!this.state.previewLoaded && !this.state.previewLoadError && (
                                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 240 }} role="status" aria-label="Cargando previsualización">
                                  <MDBSpinner size="sm" />
                                </div>
                              )}
                              <img
                                key={`${previewFile.uuid}:${selected.key}`}
                                src={selected.url}
                                alt={previewFile?.title || previewFile?.originalName || "preview"}
                                style={{ ...imagePreviewStyle, display: this.state.previewLoaded ? imagePreviewStyle.display : "none" }}
                                onLoad={() => this.setState({ previewLoaded: true, previewLoadError: false })}
                                onError={this.handlePreviewLoadError}
                              />
                            </div>
                          </div>
                        )}
                        {previewFile?.mediaType === "image" && this.state.previewLoadError && (
                          <div className="alert alert-warning mb-0" role="alert">No se pudo cargar ninguna versión de esta imagen.</div>
                        )}
                        {previewFile?.mediaType === "video" && selected?.url && (
                          <video src={selected.url} controls style={{ width: "100%", maxHeight: 460, display: "block", margin: "0 auto" }} />
                        )}
                      </div>

                      {previewCropEnabled && previewFile?.mediaType === "image" && (
                        <div className="mb-3 border rounded p-2">
                          <small className="fw-semibold d-block mb-2">Parámetros de recorte</small>
                          <MDBRow className="g-2 mb-2">
                            <MDBCol md="3" sm="6" xs="6">
                              <MDBInput
                                label="Ancho"
                                type="number"
                                min="1"
                                value={previewCropWidth}
                                onChange={(e) => this.setState({ previewCropWidth: Number(e.target.value || 0) })}
                              />
                            </MDBCol>
                            <MDBCol md="3" sm="6" xs="6">
                              <MDBInput
                                label="Alto"
                                type="number"
                                min="1"
                                value={previewCropHeight}
                                onChange={(e) => this.setState({ previewCropHeight: Number(e.target.value || 0) })}
                              />
                            </MDBCol>
                            <MDBCol md="2" sm="4" xs="4">
                              <small className="text-muted">X: {Number(previewCropX).toFixed(1)}%</small>
                              <input type="range" min="0" max="100" step="0.1" value={previewCropX}
                                     onChange={(e) => this.setState({ previewCropX: Number(e.target.value) })}
                                     className="form-range" />
                            </MDBCol>
                            <MDBCol md="2" sm="4" xs="4">
                              <small className="text-muted">Y: {Number(previewCropY).toFixed(1)}%</small>
                              <input type="range" min="0" max="100" step="0.1" value={previewCropY}
                                     onChange={(e) => this.setState({ previewCropY: Number(e.target.value) })}
                                     className="form-range" />
                            </MDBCol>
                            <MDBCol md="2" sm="4" xs="4">
                              <small className="text-muted">Zoom: {Number(previewCropZoom).toFixed(2)}</small>
                              <input type="range" min="1" max="3" step="0.01" value={previewCropZoom}
                                     onChange={(e) => this.setState({ previewCropZoom: Number(e.target.value) })}
                                     className="form-range" />
                            </MDBCol>
                          </MDBRow>
                        </div>
                      )}

                      <MDBInput
                        label="URL externa para compartir"
                        value={shareUrl || ""}
                        readOnly
                      />
                    </>
                  );
                })()}
              </MDBModalBody>
              <MDBModalFooter>
                <MDBBtn color="light" size="sm" onClick={this.copyPreviewUrl}>
                  <MDBIcon fas icon="copy" className="me-1" />{this.state.previewCopyOk ? "Copiada" : "Copiar URL"}
                </MDBBtn>
                <MDBBtn color="secondary" size="sm" onClick={this.closePreview}>Cerrar</MDBBtn>
              </MDBModalFooter>
            </MDBModalContent>
          </MDBModalDialog>
        </MDBModal>

      </MDBContainer>
    );
  }
}
